import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Icon,
  Input,
  Button,
  Avatar,
  Lozenge
} from '../../Universal/NovusDSImports';
import Card from '../../SharedComponets/Card';

import { Helmet } from 'react-helmet';

import { TableWrapper, UserGroupProfileSection } from '../globalStyles';
import TableTitle from '../../SharedComponets/TableTitle';
import { TooltipComponent } from '../../SharedComponets/Tooltip';
import {
  btnStyles,
  confirmPopupModalStyles,
  inputStyles,
  modalStyles
} from '../../Universal/NovusDSImports/variants';

import {
  getAdminUsersFetch,
  getAdminAgenciesFetch,
  getAdminGroupsFetch
} from '../../Store/reducers/AdminUsers';

import AddEditAdminUserModal from './AddEditAdminUserModal';
import ConfirmationPopupModal from './ConfirmationPopupModal';
import { useReduxSelector } from '../../Store/reduxHooks';
import { FormDataType } from './adminUsers.types';
import { STRINGS } from '../../Constants/ConstantStrings';
import { colorState } from '../../Universal/Foundation';
import { TablePopupStyles } from '../GenericTableComponent/styles';
import { RootState } from '../../store';
import {
  AddEditStyles,
  debounceFunction,
  getOrderingParam,
  getPermission,
  getUserColor,
  getUserInitials
} from '../../CommonUtilities/CommonUtilities';
import NoDataFound from '../../SharedComponets/NoDataFound';
import { EmptyUsersListIcon } from '../../Universal/Assets';
import PALoader from '../../SharedComponets/PALoader';
import { setShowTableLoader } from '../../Store/reducers/Common';

const GenericTableRT7 = lazy(
  () => import('../GenericTableComponent/GenericTable')
);

const AdminUsers: React.FC = () => {
  const dispatch = useDispatch();
  const timerToSaveDetails = useRef<NodeJS.Timeout | null>(null),
    [searchFilterValue, setSearchFilterValue] = useState<string>(''),
    // [isSearching, setIsSearching] = useState<boolean>(false),
    [isModalOpen, setIsModalOpen] = useState<boolean>(false),
    [isConfirmationModalOpen, setIsConfirmationModalOpen] =
      useState<boolean>(false),
    [editFormData, setEditFormData] = useState<FormDataType>(),
    [editMode, setEditMode] = useState<string>(),
    [userGroup, setUserGroup] = useState<boolean>(false),
    [confirmationModalType, setConfirmationModalType] = useState<string>(),
    [tableSortOrder, setTableSortOrder] = useState<
      Array<{ id: string; desc: boolean }>
    >([]),
    [agency, setAgency] = useState<Array<{ value: number; label: string }>>([]),
    [isLoadingInitialData, setIsLoadingInitialData] = useState<boolean>(true);

  const { adminUsers, isFetchingAdminUsers } = useSelector(
      (state: RootState) => state.AdminUsers
    ),
    currentUser = useReduxSelector(
      (state: RootState) => state.Common.currentUserDetails
    ),
    isMobileScreen = useReduxSelector(
      (state: RootState) => state.Common.isMobileScreen
    ),
    tableRowLimit = useReduxSelector(
      (state: RootState) => state.Common.tableRowLimit
    ),
    agencies = useSelector(
      (state: RootState) => state.AdminUsers.adminAgencies.results
    );
  const clientName = useReduxSelector(
    (state: RootState) => state.ClientInfo.client_name
  );

  useEffect(() => {
    if (agencies)
      setAgency(
        agencies.map((agency) => {
          return { value: agency.id, label: agency.name };
        })
      );
  }, [agencies]);

  useEffect(() => {
    if (adminUsers && !isFetchingAdminUsers) {
      setIsLoadingInitialData(false);
    }
  }, [adminUsers, isFetchingAdminUsers]);

  const columns = [
    {
      Header: STRINGS.NAME,
      accessor: 'email',
      Cell: ({ row }) => (
        <>
          <UserGroupProfileSection
            className="d-flex align-items-center"
            aria-label={`${STRINGS.NAME} : ${row.original.fullname}, ${STRINGS.EMAIL} : ${row.original.email}`}
          >
            <div className="me-3">
              <Avatar
                size="s"
                role="none"
                src={row.original.profile_image}
                username={getUserInitials(row.original.fullname)}
                userNameColor={getUserColor(row.original.fullname)}
                isDefault={false}
              />
            </div>
            <div className="d-flex flex-column">
              <p className="text-name">{row.original.fullname}</p>
              <p className="text-email">{row.original.email}</p>
            </div>
          </UserGroupProfileSection>
        </>
      ),
      label: ({ row }) =>
        `${STRINGS.NAME} : ${row.original.fullname}, ${STRINGS.EMAIL} : ${row.original.email}`
    },
    {
      Header: STRINGS.USER_GROUPS,
      accessor: 'groups',
      Cell: ({ row }) => {
        return (
          <section
            aria-label={`${STRINGS.USER_GROUPS} : ${row.original.groups
              .map((group) => group.name)
              .join(', ')}`}
          >
            {row.original.groups.map((group, index: number) => (
              <div key={index} aria-hidden={true}>
                {group.name}
              </div>
            ))}
          </section>
        );
      },
      label: ({ row }) =>
        `${STRINGS.USER_GROUPS} : ${row.original.groups
          .map((group) => group.name)
          .join(', ')}`
    },
    {
      Header: STRINGS.STATUS,
      accessor: 'enabled',
      Cell: ({ row }) => (
        <section
          aria-label={`${STRINGS.STATUS} : ${
            row.original.enabled ? 'User Active' : 'User Disabled'
          }`}
        >
          <Lozenge
            tabIndex={-1}
            type={row.original.enabled ? 'success' : 'warning'}
            text={row.original.enabled ? 'Active' : 'Disabled'}
          />
        </section>
      ),
      label: ({ row }) =>
        `${STRINGS.STATUS} : ${
          row.original.enabled ? 'User Active' : 'User Disabled'
        }`
    },
    {
      Header: STRINGS.AGENCY,
      accessor: 'agency',
      Cell: ({ row }) => (
        <>
          <section
            title={
              row.original.agency && agency[row.original.agency - 1]?.label
            }
            aria-label={`${STRINGS.AGENCY} : ${
              row.original.agency && agency[row.original.agency - 1]?.label
            }`}
          >
            {row.original.agency && agency[row.original.agency - 1]?.label}
          </section>
        </>
      ),
      label: ({ row }) =>
        `${STRINGS.AGENCY} : ${
          row.original.agency && agency[row.original.agency - 1]?.label
        }`
    },
    {
      Header: STRINGS.IPAWS_NOTIFICATIONS,
      Cell: ({ row }) => (
        <section
          aria-label={`Get ${STRINGS.IPAWS_NOTIFICATIONS} By: ${
            row.original.send_ipaws_email_notifications &&
            row.original.send_ipaws_sms_notifications
              ? ' Email and SMS '
              : !row.original.send_ipaws_email_notifications &&
                !row.original.send_ipaws_sms_notifications
              ? ' None '
              : row.original.send_ipaws_email_notifications
              ? ' Email '
              : ' SMS '
          }`}
        >
          {row.original.send_ipaws_email_notifications && <div>Email</div>}
          {row.original.send_ipaws_sms_notifications && <div>SMS</div>}
        </section>
      ),
      label: ({ row }) =>
        `Get ${STRINGS.IPAWS_NOTIFICATIONS} By: ${
          row.original.send_ipaws_email_notifications &&
          row.original.send_ipaws_sms_notifications
            ? ' Email and SMS '
            : !row.original.send_ipaws_email_notifications &&
              !row.original.send_ipaws_sms_notifications
            ? ' None '
            : row.original.send_ipaws_email_notifications
            ? ' Email '
            : ' SMS '
        }`
    },
    {
      Header: STRINGS.CONTACT_NUMBER,
      Cell: ({ row }) => (
        <section
          aria-label={`${STRINGS.CONTACT_NUMBER} : ${
            row.original.phone_number.country_code
              ? '+' + row.original.phone_number.country_code + ' '
              : ''
          }${row.original.phone_number.number}`}
        >
          {}
          {row.original.phone_number.country_code &&
            `+${row.original.phone_number.country_code}`}{' '}
          {row.original.phone_number.number}
        </section>
      ),
      label: ({ row }) =>
        `${STRINGS.CONTACT_NUMBER} : ${
          row.original.phone_number.country_code
            ? '+' + row.original.phone_number.country_code + ' '
            : ''
        }${row.original.phone_number.number}`
    },
    {
      Header: STRINGS.ACTIONS,
      Cell: ({ row }) => {
        return (
          <section className="view-details-section gap-3" aria-label="actions">
            <span className="visually-hidden">{STRINGS.ACTIONS}</span>
            {getPermission(currentUser, 'view_applicationuser') && (
              <div
                className="action-icons"
                role="button"
                tabIndex={0}
                aria-label={`view details of user ${row.original.fullname}`}
                data-tooltip-id={`view-${row.original.id}`}
                onClick={() => {
                  setEditMode('View');
                  setEditFormData({ ...row.original });
                  setUserGroup(false);
                  setIsModalOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setEditMode('View');
                    setEditFormData({ ...row.original });
                    setUserGroup(false);
                    setIsModalOpen(true);
                  }
                }}
              >
                <Icon icon="view_details" height="20px" width="20px" />
                <TooltipComponent
                  id={`view-${row.original.id}`}
                  place="bottom-end"
                >
                  {STRINGS.VIEW}
                </TooltipComponent>
              </div>
            )}
            {getPermission(currentUser, 'add_applicationuser') && (
              <div
                className="action-icons"
                role="button"
                tabIndex={0}
                aria-label={`edit details of user ${row.original.fullname}`}
                onClick={() => {
                  setEditMode('Edit');
                  setEditFormData({ ...row.original });
                  setUserGroup(false);
                  setIsModalOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setEditMode('Edit');
                    setEditFormData({ ...row.original });
                    setUserGroup(false);
                    setIsModalOpen(true);
                  }
                }}
                data-tooltip-id={`edit-${row.original.id}`}
              >
                <Icon icon="edit_02" height="20px" width="20px" />
                <TooltipComponent
                  id={`edit-${row.original.id}`}
                  place="bottom-start"
                >
                  {STRINGS.EDIT}
                </TooltipComponent>
              </div>
            )}
            <div
              data-tooltip-id={row.original.id}
              className="option-icon"
              aria-label={`other options for user ${row.original.fullname}`}
              tabIndex={0}
              role="button"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.currentTarget.click();
                }
              }}
            >
              <Icon icon="dots_vertical" width={'20px'} height={'20px'} />
            </div>
            <TooltipComponent
              openOnClick
              id={row.original.id}
              tooltipBgColor={colorState.surface.default.secondary}
              noPadding
              noArrow
              closeOnScroll
              place="bottom"
            >
              <TablePopupStyles>
                <ul>
                  {getPermission(currentUser, 'change_applicationuser') && (
                    <li
                      aria-label={`change user group for user ${row.original.fullname}`}
                      tabIndex={0}
                      role="button"
                      onClick={() => {
                        setUserGroup(true);
                        setEditFormData({ ...row.original });
                        setEditMode(undefined);
                        setIsModalOpen(true);
                      }}
                      onKeyDown={(event: any) => {
                        if (event.key === 'Enter') {
                          setUserGroup(true);
                          setEditFormData({ ...row.original });
                          setEditMode(undefined);
                          setIsModalOpen(true);
                        }
                      }}
                    >
                      {STRINGS.CHANGE_USER_GROUP}
                    </li>
                  )}
                  {getPermission(currentUser, 'change_user') && (
                    <li
                      aria-label={
                        row.original.enabled
                          ? `disable user ${row.original.fullname}`
                          : `enable user ${row.original.fullname}`
                      }
                      tabIndex={0}
                      role="button"
                      onClick={() => {
                        setIsConfirmationModalOpen(true);
                        setConfirmationModalType(
                          row.original.enabled
                            ? STRINGS.DISABLE
                            : STRINGS.ENABLE
                        );
                        setEditFormData({ ...row.original });
                      }}
                      onKeyDown={(event: any) => {
                        if (event.key === 'Enter') {
                          setIsConfirmationModalOpen(true);
                          setConfirmationModalType(
                            row.original.enabled
                              ? STRINGS.DISABLE
                              : STRINGS.ENABLE
                          );
                          setEditFormData({ ...row.original });
                        }
                      }}
                    >
                      {row.original.enabled
                        ? STRINGS.DISABLE_USER
                        : STRINGS.ENABLE_USER}
                    </li>
                  )}
                  {getPermission(currentUser, 'delete_applicationuser') && (
                    <li
                      aria-label={`remove user ${row.original.fullname}`}
                      tabIndex={0}
                      role="button"
                      className="text-danger"
                      onClick={() => {
                        setIsConfirmationModalOpen(true);
                        setConfirmationModalType('Remove');
                        setEditFormData({ ...row.original });
                      }}
                      onKeyDown={(event: any) => {
                        if (event.key === 'Enter') {
                          setIsConfirmationModalOpen(true);
                          setConfirmationModalType('Remove');
                          setEditFormData({ ...row.original });
                        }
                      }}
                    >
                      {STRINGS.REMOVE_USER}
                    </li>
                  )}
                </ul>
              </TablePopupStyles>
            </TooltipComponent>
          </section>
        );
      }
    }
  ];

  useEffect(() => {
    if (getPermission(currentUser, 'view_agency')) {
      dispatch(getAdminAgenciesFetch({}));
    }

    dispatch(getAdminGroupsFetch({}));
  }, [dispatch, currentUser]);

  const handleComplexTableSort = () => {
    const newSortOrder: Array<{ id: string; desc: boolean }> = [];

    for (let i = 0; i < tableSortOrder.length; i++) {
      if (tableSortOrder[i].id === 'email') {
        newSortOrder.push(
          { id: 'first_name', desc: tableSortOrder[i].desc },
          { id: 'last_name', desc: tableSortOrder[i].desc },
          tableSortOrder[i]
        );
      } else if (tableSortOrder[i].id === 'groups') {
        newSortOrder.push({ id: 'group', desc: tableSortOrder[i].desc });
      } else {
        newSortOrder.push(tableSortOrder[i]);
      }
    }
    return getOrderingParam(newSortOrder);
  };

  const getNextSetOfData = (offset: number) => {
    dispatch(
      getAdminUsersFetch({
        params: {
          offset,
          limit: tableRowLimit,
          search: searchFilterValue,
          ordering: handleComplexTableSort()
        },
        shouldAppend: true
      })
    );
  };

  useEffect(() => {
    debounceFunction(timerToSaveDetails, 100, () => {
      setIsLoadingInitialData(true);
      dispatch(
        getAdminUsersFetch({
          params: {
            limit: tableRowLimit,
            search: searchFilterValue,
            ordering: handleComplexTableSort()
          }
        })
      );
    });
  }, [searchFilterValue, tableSortOrder]);

  useEffect(() => {
    if (!isFetchingAdminUsers) {
      dispatch(setShowTableLoader({ showTableLoader: false }));
    }
  }, [isFetchingAdminUsers]);

  const customStyles = {
    content: {
      maxWidth: '40%',
      minHeight: '10vh'
    },
    overlay: {
      backgroundColor: 'rgba(114, 114, 114, 0.294)'
    }
  };

  const NoManageUsersData = () => {
    return (
      <NoDataFound
        src={EmptyUsersListIcon}
        description={STRINGS.YOUR_LIST_OF_USERS_ARE_EMPTY}
        alt={'no-manage-users-img'}
        isAbsolute
        subMsgDescription={
          STRINGS.YOUR_LIST_OF_USERS_ARE_EMPTY_BECAUSE_YOU_HAVENT_ADDED_ANY_USERS_YET
        }
        btn={
          <Button
            {...btnStyles.primary}
            onClick={() => {
              setIsModalOpen(true);
              setEditMode('Add');
            }}
          >
            <Icon
              icon={'add'}
              stroke={colorState.icon.default['primary-inverse']}
            />
            {STRINGS.ADD_NEW_USERS}
          </Button>
        }
      />
    );
  };

  const NoManageUsersFilterData = () => {
    return (
      <NoDataFound
        src={EmptyUsersListIcon}
        description={STRINGS.USER_NOT_FOUND}
        alt={'no-manage-users-img'}
        isAbsolute
        subMsgDescription={STRINGS.NO_USER_FOUND_WITH_GIVEN_VALUE}
      />
    );
  };

  return (
    <Card className="w-100">
      <Helmet>
        <title>{clientName} - Manage Users</title>
      </Helmet>
      <TableWrapper id="table-group">
        <TableTitle title={STRINGS.USER_MANAGEMENT}>
          {getPermission(currentUser, 'add_applicationuser') && (
            <Button
              title="Add New User"
              onClick={() => {
                setUserGroup(false);
                setIsModalOpen(true);
                setEditMode('Add');
              }}
              {...btnStyles.primary}
            >
              <Icon icon={'add'} stroke={'white'} />
              {STRINGS.ADD_USER}
            </Button>
          )}
        </TableTitle>
        <div className="search-input col-12 col-sm-6 col-md-4 col-lg-3">
          <Input
            name="adminSearch"
            placeholder={STRINGS.SEARCH}
            isMargin={false}
            hideLabel={true}
            role="searchbox"
            aria-label="search-users"
            inputWrapperClassName="input-field"
            value={searchFilterValue}
            iconLeft={
              <Icon
                icon={'search'}
                stroke={colorState.icon.default.secondary}
              />
            }
            onChange={(e) => {
              setSearchFilterValue(e.target.value);
            }}
            {...inputStyles}
          />
        </div>

        <div id="table">
          <div className="w-100 h-100 d-flex overflow-auto flex-grow-1 flex-column">
            {isLoadingInitialData ? (
              <PALoader />
            ) : (
              <Suspense fallback={<PALoader />}>
                <GenericTableRT7
                  columns={columns}
                  shouldFocusColumnOfRow={true}
                  tableData={adminUsers?.results || []}
                  serialNumber
                  noDataFoundScreen={NoManageUsersData()}
                  isSearching={searchFilterValue.length > 0}
                  noFilterDataFoundScreen={NoManageUsersFilterData()}
                  infiniteScrollSettings={{
                    getNextSetOfData,
                    pageSize: tableRowLimit,
                    totalCount: adminUsers?.meta?.count || 0
                  }}
                  setSelectedSortOrder={setTableSortOrder}
                  defaultSorted={tableSortOrder}
                />
              </Suspense>
            )}
          </div>
        </div>

        <AddEditAdminUserModal
          isOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          editMode={editMode}
          setEditMode={setEditMode}
          editFormData={editFormData}
          setEditFormData={setEditFormData}
          userGroup={userGroup}
          setUserGroup={setUserGroup}
          searchFilterValue={searchFilterValue}
          title={
            userGroup
              ? STRINGS.USER_GROUP
              : editMode === 'View'
              ? STRINGS.VIEW_USER_PROFILE
              : editMode === 'Edit'
              ? STRINGS.EDIT_USER_PROFILE
              : STRINGS.ADD_USER
          }
          isBodyPaddingNone={true}
          showHeader={true}
          closeModal={() => setIsModalOpen(false)}
          style={AddEditStyles(isMobileScreen)}
          {...modalStyles}
        />

        <ConfirmationPopupModal
          isOpen={isConfirmationModalOpen}
          closeModal={() => setIsConfirmationModalOpen(false)}
          editData={editFormData}
          showHeader={true}
          modalType={confirmationModalType}
          title={`${confirmationModalType} User`}
          showBorderTop={false}
          searchFilterValue={searchFilterValue}
          style={customStyles}
          {...confirmPopupModalStyles}
          isBodyPaddingNone={true}
        />
      </TableWrapper>
    </Card>
  );
};

export default AdminUsers;
